import { memo, useEffect, useRef, useState } from "react";
import { ECharts } from "echarts";
import * as echarts from "echarts";
import moment from "moment";
import DeviceMetricApi from "../../../../../../api/DeviceMetricApi";
import DeviceApi from "../../../../../../api/DeviceApi";
import SeriesColorUtils from "../../../../../../util/SeriesColorUtils";
import StringUtils from "../../../../../../util/StringUtils";
import EventManager from "../../../device/common/event-manager/EventManager";
import { ChartHeader, ChartUnit, ChartName, ChartWrapper, DeviceMetricsWrappers, DeviceMetricsWrapper, EChartsWrapper, ChartInfo, ChartCurrentValue, ChartLatestMetricTime } from "./DeviceMetrics.styled";

interface DeviceMetricProps {
    eventManager: EventManager,
    deviceId: string
}

const DeviceMetrics: React.FC<DeviceMetricProps> = (props)=> {
    let { deviceId, eventManager } = props;

    // api
    let deviceApi = new DeviceApi();
    let deviceMetricApi = new DeviceMetricApi();

    // ref
    let echartsDomsRef = useRef<HTMLElement[]>([]);
    let echartsRef =useRef<ECharts[]>([]);
    let deviceMetricsSeriesListRef = useRef([]);
    let deviceViewRef = useRef<any>(null);

    // state
    let [deviceMetricProperties, setDeviceMetricProperties] = useState([]);
    let [repaint, setRepaint] = useState(null);
    let [latestDeviceMetrics, setLatestDeviceMetrics] = useState<any>({});
    let [metricsUpdateTime, setMetricsUpdateTime] = useState<string>('');

    // functions
    let fetchRealtimeMetricsSeries = async ()=> {
        // fetch data
        let deviceMetricsSeriesList = (await deviceMetricApi.getRealtimeDeviceMetricsSeries(deviceId)).data.data;
        return deviceMetricsSeriesList;
    };

    let fetchLatestDeviceMetrics = async (deviceId: string)=> {
        let latestDeviceMetrics = (await deviceMetricApi.getLatestDeviceMetricsView(deviceId)).data.data;
        setLatestDeviceMetrics(latestDeviceMetrics);
        props.eventManager.notifyLatestDeviceUpdated(latestDeviceMetrics);
    };

    let fetchDeviceViewData = async (deviceId)=> {
        let deviceView = (await deviceApi.getDeviceViewByDeviceId(deviceId)).data.data;
        deviceViewRef.current = deviceView;
    };

    let updateSeriesData = async (deviceMetricsSeriesList: any[])=> {
        let seriesDatas = [];

        // process
        for(let i=0; i<deviceMetricsSeriesList.length; i++) {
            let deviceMetricsSeries = deviceMetricsSeriesList[i];
            let property = deviceMetricProperties[i];
            
            // series data
            let seriesData = [];
            let metrics = deviceMetricsSeries.metrics;
            let lastMetricTimestamp: number = null;
            let paddingGapMillis = 5 * 10000;
            for (let i = 0; i < metrics.length; i++) {
                let metric = metrics[i];
                
                // filter error metrics (mainly caused by sensor errors)
                let errorMetric = false;
                if (property.min != null && Number(metric.value) < property.min) {
                    errorMetric = true;
                } else if (property.max != null && Number(metric.value) > property.max) {
                    errorMetric = true;
                }
                if (errorMetric) {
                    continue;
                }
                
                // pad data point
                if (lastMetricTimestamp !=null && Math.abs(metric.timestamp - lastMetricTimestamp) > paddingGapMillis) {
                    // down data point
                    // let downDataPoint = {name: '' + (Number(lastMetricTimestamp)+1), value: [(Number(lastMetricTimestamp)+1), null]};
                    // seriesData.push(downDataPoint);

                    // up data point
                    // let upDataPoint: any = {name: '' + (Number(metric.timestamp)-1), value: [(Number(metric.timestamp)-1), null]};
                    // seriesData.push(upDataPoint);
                    seriesData.push(null);
                }
                lastMetricTimestamp = metric.timestamp;

                // add to series data
                seriesData.push({
                    name: metric.timestamp + '',
                    value: [Number(metric.timestamp), Number(metric.value)],
                });
            }

            // seriesDatas
            seriesDatas.push(seriesData);
        }

        // min/max timestam
        let minTime: number;
        let maxTime: number;
        for(let i=0; i<seriesDatas.length; i++) {
            let seriesData = seriesDatas[i];
            if (seriesData.length <= 0) {
                continue;
            }
            let minTimestamp = seriesData[0].value[0];
            let maxTimestamp = seriesData[seriesData.length-1].value[0];
            minTime = minTime == null ? minTimestamp : Math.min(minTime, minTimestamp);
            maxTime = maxTime == null ? maxTimestamp : Math.max(maxTime, maxTimestamp);
        }

        for(let i=0; i<seriesDatas.length; i++) {
            let seriesData = seriesDatas[i];
            // echarts options
            if (echartsRef.current[i] != null) {
                echartsRef.current[i].setOption({
                    xAxis: {
                        min: minTime,
                        max: maxTime,
                    },
                    series: [{
                        data: seriesData
                    }]
                });
            }
        }
    };

    let formatTimestampAxis = (value)=> {
        let text = moment(value).format('HH:mm:ss');
        return text;
    }

    // initialize ECharts wrapper dom
    let initializeWrapperDom = (dom)=> {
        if (dom == null) {
            return;
        }
        let echartsWrapperDom = dom;
        if (echartsWrapperDom) {
            echartsWrapperDom.style.gridTemplateColumns = '1fr';
            if (deviceMetricProperties.length <= 0) {
                echartsWrapperDom.style.gridTemplateRows = '1fr';
            } else {
                echartsWrapperDom.style.gridTemplateRows = `repeat(${deviceMetricProperties.length}, minmax(0,1fr))`;
                echartsWrapperDom.style.gridAutoRows = '';
            }
            resizeEcharts();
        }
    }

    
    // init ECharts
    let initECharts = (dom: HTMLElement, index: number)=> {
        if (dom == null) {
            // element unmount event
            let echarts = echartsRef.current[index];
            echarts?.dispose();
            echartsRef.current[index] = null;
            return;
        }
        if (echarts.getInstanceByDom(dom)) {
            return;
        }

        // save to reference
        echartsDomsRef.current[index]=dom;
        let seriesIndex = echartsDomsRef.current.indexOf(dom);

        // init echarts
        let chart = echarts.init(dom);
        echartsRef.current[index]=chart;

        // series
        let property = deviceMetricProperties[seriesIndex];

        // init options
        let option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                position: function (pt) {
                    return [pt[0], '10%'];
                },
                backgroundColor: "#EFEFEF",
                confine: true,
                formatter: function(params) {
                    if (params == null) {
                        return '';
                    }

                    let seriesContent = '';
                    for(let param of params) {
                        if (param.value==null) {
                            continue;
                        }

                        let errorText = '';
                        if (property.min != null && Number(param.value[1]) < property.min) {
                            errorText = '（故障）';
                        } else if (property.max != null && Number(param.value[1]) > property.max) {
                            errorText = '（故障）';
                        }
                        
                        seriesContent += `
                            <div style="">
                                <div style="padding: 5px 0px;">
                                    <span style="display: inline-block; width: 50px;">测量值:</span> 
                                    <span style="display: inline-block">${param.value[1]} ${StringUtils.nonull(property.unit)} ${errorText}</span>
                                </div>
                                <div>
                                    <span style="display: inline-block; width: 50px;">时间:</span> 
                                    <span style="display: inline-block">${moment(param.value[0]).format('YYYY-MM-DD HH:mm:ss')}</span>
                                </div>
                            </div>
                        `;
                        break;
                    }
                    let content=`
                        <div style="width: 230px;">
                            ${seriesContent}
                        </div>
                    `;
                    return content;
                },
            },
            grid: {
                x: 40,
                y: 10,
                x2: 16, /* 减小右侧内边距，使绘图区靠近 ContentCard 右边线 */
                y2: 20,
            },
            xAxis: {
                type: 'time',
                margin: 4,
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: '#CCCCCC',
                        width: 1,
                        type: 'dashed',
                    },
                },
                splitNumber: 2,
                minorTick: {
                    show: false,
                },
                axisLabel: {
                    fontSize: 12,
                    color: '#FFFFFF',
                    formatter: function (value, index) {
                        return formatTimestampAxis(value);
                    }
                },
                axisPointer: {
                    type: 'line',
                    animation: true,
                    label: {
                        show: true,
                        width: 165,
                    }
                },
            },
            yAxis: {
                type: 'value',
                boundaryGap: [0, '100%'],
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: '#CCCCCC',
                        width: 1,
                        type: 'dashed',
                    }
                },
                nameTextStyle: {
                    align: 'left',
                },
                axisLabel: {
                    fontSize: 12,
                    color: '#FFFFFF',
                },
                min: property.min != null ? parseFloat(property.min) : 'dataMin',
                max: property.max != null ? parseFloat(property.max) : 'dataMax',
            },
            dataZoom: [
                {
                  type: 'inside',
                  start: 90,
                  end: 100
                }
            ],
            series: [
                {
                    name: property.name,
                    type: 'line',
                    color: SeriesColorUtils.getSeriesColor(seriesIndex),
                    smooth: false,
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.4,
                    },
                    data: [],
                    shadowBlur: 20,
                }
            ],
        };

        chart.setOption(option);
        chart.group='DeviceRealtimeMetrics';
        echarts.connect('DeviceRealtimeMetrics');

        if (deviceMetricProperties.length == seriesIndex + 1) {
            // fetch data
            setTimeout(()=> {
                updateSeriesData(deviceMetricsSeriesListRef.current);
            });
        }
    }

    let resizeEcharts = ()=> {
        for(let chart of echartsRef.current) {
            chart?.resize();
        }
    };

    // init ECharts reference
    let initEChartsRef = useRef(initECharts);

    // effect
    useEffect(()=> {
        let refreshTaskId: any = null;
        let disposed = false;

        let init = async ()=> {
            // fetch data
            let deviceMetricsSeriesList = await fetchRealtimeMetricsSeries();
            deviceMetricsSeriesListRef.current = deviceMetricsSeriesList;
            fetchLatestDeviceMetrics(props.deviceId);
            if (deviceMetricsSeriesListRef.current && deviceMetricsSeriesListRef.current.length>0) {
                setMetricsUpdateTime(getLatestMetricTime(0));
            }

            // device template properties
            if (deviceMetricProperties.length <=0 ) {
                deviceMetricProperties = [];
                for(let deviceMetricsSeries of deviceMetricsSeriesList) {
                    deviceMetricProperties.push(deviceMetricsSeries.property);
                }

            } else {
                updateSeriesData(deviceMetricsSeriesList);
            }

            // refresh task
            refreshTaskId = window.setInterval(async ()=> {
                deviceMetricsSeriesList = await fetchRealtimeMetricsSeries();
                deviceMetricsSeriesListRef.current = deviceMetricsSeriesList;
                updateSeriesData(deviceMetricsSeriesList);
                setDeviceMetricProperties([].concat(deviceMetricProperties))

                // device view
                fetchDeviceViewData(props.deviceId);

                if (deviceMetricsSeriesListRef.current && deviceMetricsSeriesListRef.current.length>0) {
                    setMetricsUpdateTime(getLatestMetricTime(0));
                }

                if (disposed) {
                    window.clearInterval(refreshTaskId);
                }
            }, 7500);
        };

        init();

        // resize charts on window resize
        window.addEventListener('resize', resizeEcharts);

        // unload
        return ()=> {
            disposed = true;
            for(let chart of echartsRef.current) {
                chart?.dispose();
            }

            // clear interval
            if (refreshTaskId!=null) {
                window.clearInterval(refreshTaskId);
            }

            // clear resize listener
            window.removeEventListener('resize', resizeEcharts);
        }
    }, []);

    // render
    let getLatestMetricTime = (index: number)=> {
        let deviceMetricsSeries = deviceMetricsSeriesListRef.current[index];
        if (deviceMetricsSeries != 0) {
            let metrics = deviceMetricsSeries.metrics;
            if (metrics.length > 0) {
                let latestMetrics = metrics[metrics.length-1];
                let time = moment(Number(latestMetrics.timestamp)).format('YYYY/MM/DD HH:mm:ss');
                return time;
            }
        }
        return '';
    };

    let getLatestMetricValue = (index: number)=> {
        let deviceMetricProperty = deviceMetricProperties[index];
        let deviceMetricsSeries = deviceMetricsSeriesListRef.current[index];
        if (deviceMetricsSeries != 0) {
            let metrics = deviceMetricsSeries.metrics;
            if (metrics.length > 0) {
                let latestMetrics = metrics[metrics.length-1];
                let errorText = '';
                if (deviceMetricProperty.min != null && Number(latestMetrics.value) < Number(deviceMetricProperty.min)
                || deviceMetricProperty.max != null && Number(latestMetrics.value) > Number(deviceMetricProperty.max)) {
                    // errorText = ' 故障';
                    return '故障';
                }
                let text = `${latestMetrics.value}${errorText}`;
                return text;
            }
        }
        return '';
    };

    return (
        <DeviceMetricsWrappers>
             {deviceViewRef.current?.node?.status != 'ONLINE' && metricsUpdateTime && (
                    <ChartLatestMetricTime>最后更新时间：{metricsUpdateTime}</ChartLatestMetricTime>
            )}
            <DeviceMetricsWrapper ref={(dom)=> initializeWrapperDom(dom)}>
                {       
                    deviceMetricProperties.map((e, index)=> {
                        return (
                            <ChartWrapper key={e.name} onClick={()=> props.eventManager.notifyDeviceMetricSelected(e, getLatestMetricValue(index))}>
                                <ChartHeader>
                                    <ChartName>{e.name}</ChartName>
                                    <ChartCurrentValue>{getLatestMetricValue(index)}</ChartCurrentValue>
                                    <ChartInfo>
                                        <ChartUnit>单位：{StringUtils.emptyToSlash(e.unit)}</ChartUnit>
                                    </ChartInfo>
                                </ChartHeader>
                                <EChartsDom index={index} refCallback={initEChartsRef.current} />
                            </ChartWrapper>
                        )
                    })
                }           
            </DeviceMetricsWrapper>
        </DeviceMetricsWrappers>
        
    )
}

interface EChartsDomProps {
    index: number;
    refCallback: (dom: HTMLDivElement, index: number)=> void;
}

let EChartsDom: React.FC<EChartsDomProps> = memo((props)=> {
    return <EChartsWrapper ref={(dom)=> props.refCallback(dom, props.index)}>
    </EChartsWrapper>
});


export default DeviceMetrics;
